import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import { type Order, ShipmentStatus } from "@/api.types.v2";

interface PaginationMetadata {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CustomerOrdersResponse {
  data: Order[];
  metadata: PaginationMetadata;
}

/**
 * Fetch customer orders using v2 API
 * Returns orders with shipments (not parcels)
 */
export function useCustomerOrdersV2({
  page = 1,
  limit = 50,
}: {
  page?: number;
  limit?: number;
}) {
  const { token, customer } = useSession();

  return useQuery<CustomerOrdersResponse>({
    queryKey: ["customerOrdersV2", customer?.id, page, limit],
    queryFn: async () => {
      const response = await axios.get<CustomerOrdersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/me/orders`,
        {
          params: { page, limit },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    enabled: !!token && !!customer?.id,
  });
}

// Original payment prompts require a financial obligation as well as a
// Shipment awaiting preparation. Return transportation and invoices own theirs.
export function isOrderAwaitingPayment(order: Order): boolean {
  return (
    order.shipments?.[0]?.status === ShipmentStatus.PENDING_PAYMENT &&
    !order.shipments.some((shipment) => shipment.cancellationRequestedAt) &&
    ["PENDING", "FAILED", "PARTIAL"].includes(order.paymentStatus ?? "") &&
    (order.balanceCents ?? order.totalCents ?? 0) > 0
  );
}

// Helper to get primary shipment status for display
export function getOrderStatus(order: Order): ShipmentStatus | null {
  if (!order.shipments?.length) return null;
  // Use first shipment status as primary
  return order.shipments[0].status;
}

// Helper to format recipient info from shipment
export function getRecipientFromOrder(order: Order): {
  name: string;
  fullAddress: string;
  phone?: string;
  email?: string;
  company?: string;
} | null {
  if (!order.shipments?.length) return null;
  const shipment = order.shipments[0];

  // Pattern B (task #258): role-tagged addresses[] is the source of truth.
  // Note: API returns contactName/contactPhone/contactEmail, but types say name/phone/email.
  const toAddress = shipment.addresses?.find((a) => a.type === "TO");
  if (!toAddress) return null;

  const addr = toAddress.address;
  const parts = [
    addr?.line1,
    addr?.line2,
    addr?.city,
    addr?.province,
    addr?.postalCode,
  ].filter(Boolean);
  return {
    name:
      (toAddress.contactName !== undefined
        ? toAddress.contactName
        : toAddress.name) ?? "",
    company:
      toAddress.contactCompany !== undefined
        ? toAddress.contactCompany
        : toAddress.company,
    fullAddress: parts.join(", "),
    phone:
      toAddress.contactPhone !== undefined
        ? toAddress.contactPhone
        : toAddress.phone,
    email:
      toAddress.contactEmail !== undefined
        ? toAddress.contactEmail
        : toAddress.email,
  };
}

// Helper to check if order is paid
export function isOrderPaid(order: Order): boolean {
  if (!order.shipments?.length) return false;
  return order.shipments[0].status !== ShipmentStatus.PENDING_PAYMENT;
}
